import { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  MdFormatBold,
  MdFormatItalic,
  MdFormatListBulleted,
  MdFormatListNumbered,
  MdLink,
  MdFormatClear,
} from "react-icons/md";

const MAX_CHARS = 500;

export default function BasicInfoSection({
  data,
  errors,
  onChange,
  exhibitors,
  readOnly = false,
}) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: { keepMarks: true },
        orderedList: { keepMarks: true },
      }),
    ],
    editable: !readOnly,
    content: data.overview || "",
    onUpdate: ({ editor }) => {
      const text = editor.getText();

      if (text.length > MAX_CHARS) {
        editor.commands.setContent(editor.getHTML().slice(0, MAX_CHARS));
        return;
      }

      onChange({
        target: {
          name: "overview",
          value: editor.getHTML(),
        },
      });
    },
  });

  useEffect(() => {
    if (editor && data.overview !== editor.getHTML()) {
      editor.commands.setContent(data.overview || "");
    }
  }, [data.overview, editor]);

  const charCount = editor?.getText().length || 0;

  return (
    <>
      <div>
        <label className="mb-1 block text-sm font-medium">
          Title <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="title"
          value={data.title}
          onChange={onChange}
          disabled={readOnly}
          className={`w-full rounded-xl border p-3 ${
            errors.title ? "border-red-400" : "border-gray-300"
          }`}
        />
        {errors.title && (
          <p className="mt-1 text-xs text-red-500">{errors.title}</p>
        )}
      </div>

      <div className="md:col-span-2">
        <label className="mb-1 block text-sm font-medium">
          Overview <span className="text-red-500">*</span>
        </label>

        {!readOnly && (
          <div className="flex gap-1 rounded-t-xl border border-b-0 bg-gray-50 px-2 py-1">
            <select
              className="mr-1 rounded-lg border bg-white px-2 py-1 text-sm focus:outline-none"
              value={
                editor?.isActive("heading", { level: 1 })
                  ? "1"
                  : editor?.isActive("heading", { level: 2 })
                  ? "2"
                  : editor?.isActive("heading", { level: 3 })
                  ? "3"
                  : "p"
              }
              onChange={(e) => {
                const v = e.target.value;
                if (v === "p") {
                  editor.chain().focus().setParagraph().run();
                } else {
                  editor
                    .chain()
                    .focus()
                    .toggleHeading({ level: Number(v) })
                    .run();
                }
              }}
            >
              <option value="p">Normal</option>
              <option value="1">Heading 1</option>
              <option value="2">Heading 2</option>
              <option value="3">Heading 3</option>
            </select>
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleBold().run()}
              active={editor?.isActive("bold")}
              icon={<MdFormatBold />}
            />
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleItalic().run()}
              active={editor?.isActive("italic")}
              icon={<MdFormatItalic />}
            />
            <ToolbarButton
              onClick={() =>
                editor.chain().focus().toggleBulletList().run()
              }
              active={editor?.isActive("bulletList")}
              icon={<MdFormatListBulleted />}
            />
            <ToolbarButton
              onClick={() =>
                editor.chain().focus().toggleOrderedList().run()
              }
              active={editor?.isActive("orderedList")}
              icon={<MdFormatListNumbered />}
            />
            <ToolbarButton
              onClick={() => {
                const url = prompt("Enter URL");
                if (url) {
                  editor.chain().focus().setLink({ href: url }).run();
                }
              }}
              active={editor?.isActive("link")}
              icon={<MdLink />}
            />
            <ToolbarButton
              onClick={() =>
                editor.chain().focus().clearNodes().unsetAllMarks().run()
              }
              icon={<MdFormatClear />}
            />
          </div>
        )}

        {/* Editor */}
        <div
          className={`rounded-xl border transition ${
            errors.overview
              ? "border-red-400"
              : "border-gray-300 focus-within:border-blue-400 focus-within:ring-1 focus-within:ring-blue-300"
          }`}
        >
          <EditorContent
            editor={editor}
            className="overview-editor min-h-[160px] p-4"
          />
        </div>

        {/* Footer */}
        <div className="mt-1 flex items-center justify-between">
          {errors.overview ? (
            <p className="text-xs text-red-500">{errors.overview}</p>
          ) : (
            <span />
          )}
          <p
            className={`text-xs ${
              charCount >= MAX_CHARS
                ? "text-red-500"
                : "text-gray-500"
            }`}
          >
            {charCount}/{MAX_CHARS}
          </p>
        </div>
      </div>

      {/* Exhibitor Category */}
      <div className="md:col-span-2">
        <label className="mb-1 block text-sm font-medium">
          Exhibitor Category <span className="text-red-500">*</span>
        </label>
        <select
          name="exhibitor_category"
          value={data.exhibitor_category}
          onChange={onChange}
          disabled={readOnly}
          className={`w-full rounded-xl border p-3 ${
            errors.exhibitor_category
              ? "border-red-400"
              : "border-gray-300"
          }`}
        >
          <option value="">Select Category</option>
          {exhibitors?.map((c) => (
            <option key={c._id} value={c._id}>
              {c.name}
            </option>
          ))}
        </select>

        {errors.exhibitor_category && (
          <p className="mt-1 text-xs text-red-500">
            {errors.exhibitor_category}
          </p>
        )}
      </div>
    </>
  );
}

/* ---------------- Toolbar Button ---------------- */
function ToolbarButton({ icon, onClick, active }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md p-2 text-lg transition ${
        active
          ? "bg-blue-100 text-blue-600"
          : "text-gray-600 hover:bg-gray-200"
      }`}
    >
      {icon}
    </button>
  );
}
